import React from 'react';
import { useAppSelector } from 'src/redux/hooks';
import { PageHeaderV2Props } from '@amzn/seller-university-ui-components';
import { selectLearningPathwayById } from 'src/redux/slices/learningPathways';
import { selectIsWeblabInT1 } from 'src/redux/slices/weblabs';
import { WeblabId } from 'src/constants/weblab';
import { AppState } from 'src/redux/store';
import { useClientId, UNGATED_CLIENT_ID } from 'src/contexts/ClientContext';
import LearningPathwayPageHeaderV2 from './pageHeaderV2';
import LearningPathwayAccordionV2 from './moduleSectionAccordionV2';
import { CredentialAssessment } from './ModuleSectionItemV2';

export interface LearningPathwayV2Props {
  learningPathwayId: string
  onModuleSelect: (moduleId: string) => void
  hideSectionLabels?: boolean
  assessment?: CredentialAssessment
  progress?: PageHeaderV2Props['progress']
  /** When true, modules and the assessment unlock serially: each is gated until the previous item is complete. */
  enforceSerialProgression?: boolean
}

/**
 * LearningPathwayV2 renders the credential experience: the pathway header with an optional
 * completion progress bar and the V2 section accordion, which appends the credential assessment
 * to the final section. It consumes the forked V2 card and section components so the existing
 * Learning Pathway experience is unaffected.
 */
const LearningPathwayV2 = ({
  learningPathwayId,
  onModuleSelect,
  hideSectionLabels = false,
  assessment,
  progress,
  enforceSerialProgression = false,
}: LearningPathwayV2Props) => {
  const pathway = useAppSelector(
    (state: AppState) => selectLearningPathwayById(state, learningPathwayId),
  );

  const clientId = useClientId();
  const isPolyMigrationT1 = useAppSelector(
    (state: AppState) => selectIsWeblabInT1(state, WeblabId.NalandaPolyMigration),
  );
  const isLocalized = clientId !== UNGATED_CLIENT_ID || isPolyMigrationT1;

  return (
    <>
      <LearningPathwayPageHeaderV2
        title={pathway.data.title}
        description={pathway.data.description}
        imageSrc={pathway.data.imageSrc}
        stats={pathway.data.learningPathwayStats}
        isLocalized={isLocalized}
        progress={progress}
      />

      <LearningPathwayAccordionV2
        sections={pathway.data.sections}
        onModuleSelect={onModuleSelect}
        isLocalized={isLocalized}
        hideSectionLabels={hideSectionLabels}
        assessment={assessment}
        enforceSerialProgression={enforceSerialProgression}
      />
    </>
  );
};

export default LearningPathwayV2;
